import i18n from '@/i18n'
import { getAtPath } from './configPaths'
import { matchesCondition } from './conditions'
import { validateSliderConfiguration } from './sliderValidation'

function isBlank(value) {
  return value === null || value === undefined || value === ''
}

function validateField(field, value, errors, path, configuration, item = null, options = {}) {
  const rules = field.validation || {}
  const isVisible = matchesCondition(field.visibleWhen, configuration, item)
  if (!isVisible) return

  // Clickout is an optional editor field, including for older sealed schemas, except that a
  // save with Own Website needs its address (AD-252: no longer checked by Settings).
  if (path === 'ad.clickoutUrl' && (isBlank(value) || (typeof value === 'string' && value.trim() === ''))) {
    if (options.saving && configuration?.campaign?.clickoutMode === 'own-website') errors[path] = i18n.t('editorV2.core.validation.websiteRequired')
    return
  }

  if (field.type === 'video-editor') {
    if (rules.required && !value?.sourceAssetId && !options.allowEmptyMedia) errors[path] = i18n.t('editorV2.core.validation.required', { label: field.label })
    return
  }

  if (field.type === 'image-editor') {
    if (rules.required && !value?.sourceAssetId) {
      if (!options.allowEmptyMedia) errors[path] = i18n.t('editorV2.core.validation.required', { label: field.label })
      return
    }
    // Only a crop profile has a crop to apply. A logo or favicon ('brand-logo', 'brand-favicon':
    // contain) is placed whole; its rendition is redrawn when the logo size changes.
    const profileMode = options.assetProfiles?.[field.profile]?.mode
    const needsCrop = profileMode ? profileMode === 'crop' : field.profile !== 'logo'
    if (
      value?.sourceAssetId
      && needsCrop
      && !value?.crop
      && !value?.renditionAssetId
    ) {
      errors[path] = i18n.t('editorV2.core.validation.applyCrop', { label: field.label, labelLower: field.label.toLowerCase() })
    }
    return
  }

  if (rules.required && isBlank(value)) {
    errors[path] = i18n.t('editorV2.core.validation.required', { label: field.label })
    return
  }
  if (isBlank(value)) return

  const plainValue = field.type === 'rich-text'
    ? String(value).replace(/<[^>]+>/g, '').replace(/\u00ad/g, '')
    : String(value).replace(/\u00ad/g, '')
  if (rules.minLength && plainValue.length < rules.minLength) {
    errors[path] = i18n.t('editorV2.core.validation.tooShort', { label: field.label })
  }
  if (rules.maxLength && plainValue.length > rules.maxLength) {
    errors[path] = i18n.t('editorV2.core.validation.tooLong', { label: field.label })
  }
  if (field.type === 'url') {
    try {
      const parsed = new URL(value)
      if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error()
    } catch {
      errors[path] = i18n.t('editorV2.core.validation.invalidUrl', { label: field.label })
    }
  }
  if (rules.min != null && Number(value) < rules.min) errors[path] = i18n.t('editorV2.core.validation.tooSmall', { label: field.label })
  if (rules.max != null && Number(value) > rules.max) errors[path] = i18n.t('editorV2.core.validation.tooLarge', { label: field.label })
}

// allowEmptyMedia: an empty required picture or clip is not an error yet. A generated ad may
// leave one empty, and the editor asks for it before saving. saving: the checks only a save makes.
export function validateConfiguration(schema, configuration, options = {}) {
  const errors = {}
  if (!schema || !configuration) return errors

  ;(schema.sections || []).forEach((section) => {
    if (!matchesCondition(section.visibleWhen, configuration)) return
    ;(section.fields || []).forEach((field) => {
      validateField(
        field,
        getAtPath(configuration, field.path),
        errors,
        field.path,
        configuration,
        null,
        options,
      )
    })
  })

  ;(schema.collections || []).forEach((collection) => {
    const owningSection = (schema.sections || []).find((section) => section.collection === collection.id)
    if (owningSection && !matchesCondition(owningSection.visibleWhen, configuration)) return
    const items = getAtPath(configuration, collection.path, [])
    if (items.length < collection.minItems) {
      errors[collection.path] = i18n.t('editorV2.core.validation.minItems', {
        count: collection.minItems, label: collection.itemLabel, labelLower: collection.itemLabel.toLowerCase(),
      })
    }
    if (items.length > collection.maxItems) {
      errors[collection.path] = i18n.t('editorV2.core.validation.maxItems', {
        max: collection.maxItems, label: collection.itemLabel, labelLower: collection.itemLabel.toLowerCase(),
      })
    }
    items.forEach((item, index) => {
      ;(collection.itemSections || []).forEach((section) => {
        ;(section.fields || []).forEach((field) => {
          const path = `${collection.path}.${index}.${field.path}`
          validateField(field, getAtPath(item, field.path), errors, path, configuration, item, options)
        })
      })
    })
  })

  return { ...errors, ...validateSliderConfiguration(configuration) }
}
